/**
 * A UUID v4. `crypto.randomUUID` needs a secure context, which every surface
 * Ondit serves from has: https in production, localhost in development.
 */
export function generateId(): string {
  return crypto.randomUUID()
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Validates whether a string is a well-formed UUID (any version).
 */
export function isValidUuid(value: string): boolean {
  return UUID_RE.test(value)
}

const URL_SAFE_ALPHABET = 'useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict'

/**
 * Generates a short, URL-safe random ID.
 *
 * Replaces `nanoid` — uses `crypto.getRandomValues()` which works in all
 * contexts including non-secure (HTTP) browsers.
 *
 * @param size - Length of the generated ID (default: 21)
 * @param alphabet - Optional custom alphabet (replaces nanoid's `customAlphabet`).
 *                   Length must be in [2, 256].
 * @returns A random string drawn from the alphabet
 */
export function generateShortId(size = 21, alphabet: string = URL_SAFE_ALPHABET): string {
  const alphabetLength = alphabet.length
  if (alphabetLength < 2 || alphabetLength > 256) {
    throw new Error('generateShortId alphabet length must be between 2 and 256')
  }

  const mask = (2 << (31 - Math.clz32((alphabetLength - 1) | 1))) - 1
  const step = Math.ceil((1.6 * mask * size) / alphabetLength)

  let id = ''
  while (id.length < size) {
    const bytes = new Uint8Array(step)
    crypto.getRandomValues(bytes)
    for (let i = 0; i < step && id.length < size; i++) {
      const index = bytes[i] & mask
      if (index < alphabetLength) {
        id += alphabet[index]
      }
    }
  }
  return id
}
